// src/hooks/usePermissions.js
import { useMemo } from 'react';
import { useAuth } from './useAuth';

export const usePermissions = () => {
  const { user } = useAuth();

  // Extract permissions from user.role.permissions array from API response
  const userPermissions = useMemo(() => {
    if (!user || !user.role || !user.role.permissions) return [];
    
    // Return the permissions array directly from the API response
    return user.role.permissions;
  }, [user]);

  // Get user role name for display purposes
  const userRole = useMemo(() => {
    if (!user || !user.role) return null;
    return user.role.name;
  }, [user]);

  // Get business information
  const userBusiness = useMemo(() => {
    if (!user || !user.business) return null;
    return user.business;
  }, [user]);

  const hasPermission = (permission) => {
    if (!permission || !user || !userPermissions) return false;
    return userPermissions.includes(permission);
  };

  const hasAnyPermission = (permissions) => {
    if (!permissions || !Array.isArray(permissions) || !user || !userPermissions) return false;
    return permissions.some(permission => userPermissions.includes(permission));
  };

  const hasAllPermissions = (permissions) => {
    if (!permissions || !Array.isArray(permissions) || !user || !userPermissions) return false;
    return permissions.every(permission => userPermissions.includes(permission));
  };

  const canAccess = (requiredPermissions) => {
    if (!requiredPermissions) return true;
    
    if (Array.isArray(requiredPermissions)) {
      return hasAnyPermission(requiredPermissions);
    }
    
    return hasPermission(requiredPermissions);
  };

  // Helper function to check if user is admin (useful for certain UI elements)
  const isAdmin = () => {
    return userRole === 'Admin';
  };

  // Helper function to get role ID
  const getRoleId = () => {
    if (!user || !user.role) return null;
    return user.role.id;
  };

  // Helper function to get business ID
  const getBusinessId = () => {
    if (!user || !user.business) return null;
    return user.business.id;
  };

  return {
    userPermissions,
    userRole,
    userBusiness,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    isAdmin,
    getRoleId,
    getBusinessId
  };
};